import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PARSERS } from 'hb-react/shared/hooks/useParsedQueryParams/constants/parsers';
import {
  type ParamTypesOf,
  type Updaters,
} from 'hb-react/shared/hooks/useParsedQueryParams/types';

interface UseParsedQueryParamsProps<T extends Record<string, any>> {
  onBeforeUpdate?: () => void;
  paramTypes: ParamTypesOf<T>;
}

/**
 * Parses the query params matching the paramTypes provided
 * into a typed object.
 */
export const useParsedQueryParams = <T extends Record<string, any>>({
  paramTypes,
  onBeforeUpdate,
}: UseParsedQueryParamsProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedParams: T = useMemo(() => {
    const result: T = {} as T;

    Object.entries(paramTypes).forEach(([name, { type, defaultValue }]) => {
      const rawValue = searchParams.get(name.toString());
      const parsedValue = PARSERS[type](rawValue);

      if (parsedValue !== undefined) {
        (result as any)[name] = parsedValue;

        return;
      }

      // Fallback to the default value if any
      if (defaultValue !== undefined) {
        (result as any)[name] = defaultValue;
      }
    });

    return result;
  }, [searchParams, paramTypes]);

  // WARNING: This only checks the params that match the paramTypes provided.
  //          if other searchParams are present, they will NOT be considered.
  const hasParsedParams = useMemo(
    () =>
      Object.keys(parsedParams).length > 0 &&
      Object.entries(parsedParams).some(
        ([key, value]) => value !== paramTypes[key]?.defaultValue,
      ),
    [parsedParams, paramTypes],
  );

  // Little wrapper around setSearchParams to update a single param
  // and remove it if the value is null or undefined
  const updateParam = useCallback(
    (name: string, value?: unknown) => {
      const paramType = paramTypes[name];

      if (!paramType) {
        throw new Error(`Param type not found: ${name.toString()}`);
      }

      onBeforeUpdate?.();

      setSearchParams(() => {
        // Use window.location.search to get the most up-to-date search parameters
        // This avoids stale closure issues with the prev parameter
        const params = new URLSearchParams(window.location.search);

        // The default value is automatically applied inside the parsedParams useMemo above
        // so we don't need to set it in the search params. This keeps the search params
        // as clean as possible.
        if (value === paramType.defaultValue) {
          params.delete(name);

          return params;
        }

        const stringValue =
          value === null || value === undefined ? value : String(value);

        if (!stringValue) {
          params.delete(name);
        } else {
          params.set(name, stringValue);
        }

        return params;
      });
    },
    [onBeforeUpdate, paramTypes, setSearchParams],
  );

  const clearParams = useCallback(() => {
    setSearchParams(() => {
      // Use window.location.search to get the most up-to-date search parameters
      // This avoids stale closure issues with the prev parameter
      const params = new URLSearchParams(window.location.search);

      Object.keys(paramTypes).forEach((name) => {
        params.delete(name);
      });

      return params;
    });
  }, [setSearchParams, paramTypes]);

  // For convenience, in case you want to have a method that takes a value and updates a specific param
  const updaters = useMemo(
    () =>
      Object.keys(paramTypes).reduce(
        (acc, name) => {
          const key = name as keyof T;
          const updaterName =
            `update${key.toString().charAt(0).toUpperCase() + key.toString().slice(1)}` as `update${Capitalize<string & keyof T>}`;

          return {
            ...acc,
            [updaterName]: (value: T[typeof key]) =>
              updateParam(key.toString(), value),
          };
        },
        {} as Updaters<Required<T>>,
      ),
    [paramTypes, updateParam],
  );

  return {
    parsedParams,
    updateParam,
    hasParsedParams,
    clearParams,
    updaters,
  };
};
